import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { widgetsNoDatasetOutput } from '../WidgetLibraryGen2';
import { taskStatusRefresh } from '../gen2_widgets/task_status/TaskStatusWidget';
import { assignedTasksRefresh } from '../gen2_widgets/assigned_tasks_widget/assignedTasksRefresh.js';
import { fetchJson } from '../../../helpers/fetch.js';

import {
    dataset_refresh_path,
    dashboard_refresh_datasources_path
} from '~/routes';

const refreshPrefix = {
    WorkflowStatusWidget: ['mrs_dashboard_report_phase_data'],
    WorkflowDataStripWidget: ['workflow_summary']
}

// Dataset currently has 2 layer caching, backend is already cached so need to call dataset_refresh_path first
// Then invalidate requests
const refreshDataset = async (settings, queryClient, widgetType) => {
    const {datasets = [], wholeDatasets = []} = settings;
    const currentDatasets = widgetsNoDatasetOutput.includes(widgetType) ? wholeDatasets : datasets;
    const dataset_uuids = datasets.map((d) => d.dataset_id.split('_')[0]).filter(Boolean);
    const datasetIds = [...new Set(currentDatasets.flatMap((d) => [d.dataset_id, d.target_dataset_id]).filter((id) => id))];
    jQuery
    .post(dataset_refresh_path(), { dataset_uuids: dataset_uuids }, (result) => {
        queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'get_dataset' && datasetIds.includes(query.queryKey[1])});
    })
    .fail(() => {
        alert(I18n.t("dataset_refresh_failed"));
    });
}

const refreshDatasource = async (settings, queryClient, widgetType, {dashboardId, projectId, datasourceIds}) => {
    if (!datasourceIds || !datasourceIds.length || !dashboardId) return;

    const refresh = async (force = false) => {
        const res = await fetchJson(dashboard_refresh_datasources_path({id: dashboardId, force, project_id: projectId, _options: true}), {
            method: 'POST',
            body: JSON.stringify({datasource_ids: datasourceIds})
        });

        if (res.status === 'already_refreshed' && !force) {
            window.ToastNotification({
                message: datasourceIds.length > 1
                    ? I18n.t('datasources_already_refreshed_past_hour')
                    : I18n.t('datasource_already_refreshed_past_hour'),
                anchorElement: {
                    href: '#',
                    name: I18n.t('click_here_to_force'),
                    onClick: async (e) => {
                        e.preventDefault();
                        await refresh(true);
                    }
                },
                horizontal: 'center',
                vertical: 'top',
                autoHideDuration: 5
            });
            return;
        }

        window.ToastNotification({ message: `${I18n.t('refreshing')}...`, horizontal: 'center', vertical: 'top', autoHideDuration: 5 });
    };

    await refresh();
};

const customRefresh = {
    TaskStatusWidget: taskStatusRefresh,
    BarChartDatasetWidget: refreshDataset,
    CumulativeLineDatasetWidget: refreshDataset,
    ValuesAchievedDatasetWidget: refreshDataset,
    SimplePieDatasetWidget: refreshDataset,
    AssignedTasksWidget: assignedTasksRefresh,
    GridWidget: refreshDatasource,
    CustomChartWidget: refreshDatasource,
    DataStripDatasetWidget: refreshDatasource,
    DonutWithTargetDatasetWidget: refreshDatasource,
    GaugeWidget: refreshDatasource,
    MapWidget: refreshDatasource,
}

export const widgetsWithRefresh = [...Object.keys(refreshPrefix), ...Object.keys(customRefresh)];

// Refresh is just manually invalidate the request cached by tanstack query
// However, you will need to write what are the queryKey need to be invalidated
// Currently queryKey rule is [Unique identifier of request for path, params 1, ..., params n, widgetId]
// Therefore you need to define here what prefix to be invalidated in the refreshing widget
export const useRefreshData = (widgetId, widgetType) => {
    const queryClient = useQueryClient();
    const settings = useSelector(state => state.dashboard.widgets[widgetId].settings) || {};
    const dashboardId = useSelector(state => state.dashboard.dashboardId);
    const projectId = useSelector(state => state.dashboard.projectId);
    const datasourceId = useSelector(state => state.dashboard.widgets[widgetId]?.data_datasource?.id);

    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({
        predicate: (query) => {
            const [prefix, ...rest] = query.queryKey;
            const lastElement = rest[rest.length - 1];
            return refreshPrefix[widgetType].includes(prefix) && lastElement === widgetId;
        }
        });
    }, [queryClient, widgetId, widgetType]);

    const opts = {
        dashboardId,
        projectId,
        ...(datasourceId && { datasourceIds: [datasourceId] })
    };
    const finalRefresh = useCallback(() => {
        return customRefresh.hasOwnProperty(widgetType) ? customRefresh[widgetType](settings, queryClient, widgetType, opts) : refresh();
    }, [queryClient, settings]);

    return { refresh: finalRefresh };
}

export const useCompleteRefresh = () => {
    const queryClient = useQueryClient();
    const dashboardId = useSelector(state => state.dashboard.dashboardId);
    const projectId = useSelector(state => state.dashboard.projectId);
    const widgets = useSelector((state) => state.dashboard.widgets);

    const refresh = async (widgetId, widgetType) => {
        await queryClient.invalidateQueries({
            predicate: (query) => {
                const [prefix, ...rest] = query.queryKey;
                const lastElement = rest[rest.length - 1];
                return refreshPrefix[widgetType].includes(prefix) && lastElement === widgetId;
            }
        });
    }

    const completeRefresh = async () => {
        const datasourceIds = [...new Set(Object.values(widgets)
            .filter((widget) => customRefresh[widget.widget_type] === refreshDatasource)
            .map((widget) => widget?.data_datasource?.id)
            .filter(Boolean))];

        await Promise.all([
            datasourceIds.length && refreshDatasource({}, queryClient, null, {
                dashboardId,
                projectId,
                datasourceIds
            }),
            ...Object.entries(widgets).map(([widgetId, widget]) => {
                const widgetType = widget.widget_type;
                const settings = widget.settings || {};
                const refreshFunction = customRefresh[widgetType];

                if (refreshFunction === refreshDataset || refreshFunction === refreshDatasource) {
                    return;
                }

                return refreshFunction ? refreshFunction(settings, queryClient, widgetType, { dashboardId, projectId }) : refresh(widgetId, widgetType);
            })
        ]);
    }

    return { completeRefresh };
};